/*!

=========================================================
* Paper Dashboard PRO React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-dashboard-pro-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Nav, Collapse } from 'reactstrap'
// javascript plugin used to create scrollbars on windows
import PerfectScrollbar from 'perfect-scrollbar'
import { withAuthentication } from '../Session'
import SidebarAccount from './SidebarAccount'
import EventsBadge from './EventsBadge'
import InquiriesBadge from './InquiriesBadge'
import withAppdata from '../AppConfig/withAppdata'
import { effectiveFavorites, isFavorite, moveFavorite, resolveFavorites, toggleFavorite } from '../utils/menuFavorites'

import logo from '../../assets/img/logos/cintoia.png'
import { isMobile } from 'react-device-detect'
var ps

// The /config collapse (Asetukset) is drawn in the footer instead of the main list.
const SETTINGS_PATH = '/config'

class Sidebar extends React.Component {
  constructor(props) {
    super(props)
    this.sidebar = React.createRef()
    this.state = {
      ...this.getCollapseStates(props.routes),
      featureOptions: this.props.customerdata.features,
      // the list just saved, shown until the prefs listener brings it back
      pendingFavorites: null,
      dragFrom: null,
      dragOver: null,
    }
  }
  // this creates the intial state of this component based on the collapse routes
  // that it gets through this.props.routes
  getCollapseStates = (routes) => {
    let initialState = {}
    routes.map((prop, key) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: this.getCollapseInitialState(prop.views),
          ...this.getCollapseStates(prop.views),
          ...initialState,
        }
      }
      return null
    })
    return initialState
  }
  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.jsx - route /admin/regular-forms
  getCollapseInitialState(routes) {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && this.getCollapseInitialState(routes[i].views)) {
        return true
      } else if (window.location.pathname.indexOf(routes[i].path) !== -1) {
        return true
      }
    }
    return false
  }

  // Icon for a top-level item: Material Symbol when the route has one, else the old
  // iconcomponent / Nucleo icon so routes can migrate one at a time. Sub-items have none.
  renderIcon = (prop, submenu) => {
    if (submenu) return null
    if (prop.symbol) {
      return <span className='material-symbols-rounded sidebar-icon'>{prop.symbol}</span>
    }
    if (prop.iconcomponent) {
      const Component = prop.iconcomponent
      return <Component className='sidebar-icon' />
    }
    if (prop.icon) return <i className={prop.icon + ' sidebar-icon'} />
    return null
  }

  renderLabel = (prop) => {
    const ServiceComp = prop.serviceIcon
    return (
      <span className='sidebar-label'>
        <span className='sidebar-name'>{prop.name}</span>
        {ServiceComp ? <ServiceComp fontSize={'small'} /> : null}
        {prop.beta ? <span className='sidebar-beta'>Beta</span> : null}
        {prop.badge === 'events' ? <EventsBadge /> : null}
        {prop.badge === 'inquiries' ? <InquiriesBadge /> : null}
      </span>
    )
  }

  // Whether the menu shows a route: not invisible, and a `limited` one only when the venue has
  // its limitedmenus key on. The Suosikit section uses the same rule.
  isShown = (x) => {
    if (x.invisible) {
      return false
    }
    if (x.limited) {
      const limitedmenus = this.state.featureOptions && this.state.featureOptions.limitedmenus
      if (!limitedmenus) return false
      // The key is the route path with EVERY slash stripped: '/shop/products' reads
      // limitedmenus.shopproducts.
      if (limitedmenus[x.path.replaceAll('/', '')] === true) return true
      // Legacy fallback: this used to be replace('/', ''), which drops only the FIRST
      // slash, so config written against the old behaviour spells the two nested paths
      // 'shop/products' and 'config/proshop'. Remove once venuemanagerfeatures has been
      // migrated - for every single-segment path the two spellings are identical anyway.
      return limitedmenus[x.path.replace('/', '')] === true
    }
    return true
  }

  // Suosikit (docs/features/menu-favorites.md): admins only, so the cashier, partner and
  // cooperative menus stay as they are.
  favoritesEnabled = () => (this.props.routes || []).some((r) => r.layout === '/admin')

  userFavorites = () => {
    const prefs = this.props.appdata && this.props.appdata.userpreferences
    return prefs ? prefs.favorites : undefined
  }

  currentFavorites = () => {
    if (this.state.pendingFavorites) return this.state.pendingFavorites
    const venueDefault = this.state.featureOptions && this.state.featureOptions.favorites
    return effectiveFavorites(this.userFavorites(), venueDefault)
  }

  // The first change on top of a venue default saves the whole resulting list, so from then on
  // the user's own list is the one that counts.
  saveFavorites = (next) => {
    this.setState({ pendingFavorites: next })
    this.props.firebase.savePersonVenueManagerConfigs({ favorites: next }).catch((e) => {
      console.error('Saving favourites failed', e)
      this.setState({ pendingFavorites: null })
    })
  }

  toggleStar = (e, path) => {
    e.preventDefault()
    e.stopPropagation()
    this.saveFavorites(toggleFavorite(this.currentFavorites(), path))
  }

  renderStar = (path) => {
    if (!this.favoritesEnabled() || !path || path === '/noop') return null
    const pinned = isFavorite(this.currentFavorites(), path)
    const label = pinned ? 'Poista suosikeista' : 'Lisää suosikkeihin'
    return (
      <button
        type='button'
        className={'sidebar-star' + (pinned ? ' pinned' : '') + (isMobile ? ' touch' : '')}
        title={label}
        aria-label={label}
        aria-pressed={pinned}
        onClick={(e) => this.toggleStar(e, path)}
      >
        <span className='material-symbols-rounded'>star</span>
      </button>
    )
  }

  dropFavorite = (to) => {
    const from = this.state.dragFrom
    this.setState({ dragFrom: null, dragOver: null })
    if (from === null || from === to) return
    this.saveFavorites(moveFavorite(this.currentFavorites(), from, to))
  }

  renderFavorites = () => {
    if (!this.favoritesEnabled()) return null
    const items = resolveFavorites(this.props.routes || [], this.currentFavorites(), this.isShown)
    if (items.length === 0) {
      // a nudge only for someone who has never had a list at all
      if (this.userFavorites() !== undefined || this.state.pendingFavorites) return null
      return (
        <div className='sidebar-favorites-hint'>
          Lisää usein käyttämäsi valikot suosikeiksi tähdellä <span className='material-symbols-rounded'>star</span>
        </div>
      )
    }
    return (
      <Nav className='sidebar-favorites'>
        <li className='sidebar-divider'>
          <span className='sidebar-divider-label'>Suosikit</span>
        </li>
        {items.map((item, index) => (
          <li
            key={item.path}
            className={this.activeRoute(item.layout + item.path) + (this.state.dragOver === index ? ' drag-over' : '')}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move'
              this.setState({ dragFrom: index })
            }}
            onDragOver={(e) => {
              e.preventDefault()
              if (this.state.dragOver !== index) this.setState({ dragOver: index })
            }}
            onDrop={(e) => {
              e.preventDefault()
              this.dropFavorite(index)
            }}
            onDragEnd={() => this.setState({ dragFrom: null, dragOver: null })}
          >
            <NavLink to={item.layout + item.path} className='sidebar-item' title={item.parentName ? item.parentName + ' › ' + item.name : item.name}>
              {this.renderIcon(item, false)}
              {this.renderLabel(item)}
            </NavLink>
            {this.renderStar(item.path)}
          </li>
        ))}
        <li className='sidebar-divider'>
          <hr />
        </li>
      </Nav>
    )
  }

  // this function creates the links and collapses that appear in the sidebar (left menu)
  // padMobile: the isMobile /noop spacer rows belong at the end of the scrolling main list,
  // not after Asetukset in the footer.
  createLinks = (routes, submenu, padMobile = true) => {
    if (padMobile && isMobile && routes[routes.length - 1].path !== '/noop') {
      let i = 0
      while (i < 2) {
        routes.push(
          {
            path: '/noop',
            name: '',
            icon: '',
            layout: '/admin',
          }
        )
        i++
      }
    }

    return routes.filter((x) => x.divider || this.isShown(x)).map((prop, key) => {
      if (prop.redirect) {
        return null
      }

      // Handle divider rendering
      if (prop.divider) {
        return (
          <li key={key} className="sidebar-divider">
            {prop.label ? (
              <span className="sidebar-divider-label">{prop.label}</span>
            ) : (
              <hr />
            )}
          </li>
        )
      }

      if (prop.collapse) {
        var st = {}
        st[prop['state']] = !this.state[prop.state]
        return (
          <li className={this.getCollapseInitialState(prop.views) ? 'active' : ''} key={key}>
            <a
              href='#noop'
              className='sidebar-item'
              data-toggle='collapse'
              aria-expanded={!!this.state[prop.state]}
              onClick={(e) => {
                e.preventDefault()
                this.setState(st)
              }}
            >
              {this.renderIcon(prop, submenu)}
              {this.renderLabel(prop)}
              <span className='sidebar-chevron' />
            </a>
            <Collapse isOpen={this.state[prop.state]}>
              <ul className='nav'>{this.createLinks(prop.views, true, padMobile)}</ul>
            </Collapse>
          </li>
        )
      }

      // main level
      return (
        <li className={this.activeRoute(prop.layout + prop.path)} key={key}>
          <NavLink to={prop.layout + prop.path} className='sidebar-item'>
            {this.renderIcon(prop, submenu)}
            {this.renderLabel(prop)}
          </NavLink>
          {this.renderStar(prop.path)}
        </li>
      )
    })
  }
  // verifies if routeName is the one active (in browser input)
  // Match the exact path or a sub-path (routeName + '/') so that e.g.
  // '/admin/benefitcards' does not also match '/admin/benefitcardslist'.
  activeRoute = (routeName) => {
    const path = this.props.location.pathname
    return path === routeName || path.startsWith(routeName + '/') ? 'active' : ''
  }
  componentDidMount() {
    // console.log('setting stage')
    this.setState({ customer: null, clubdata: null })
    // if you are using a Windows Machine, the scrollbars will have a Mac look
    if (navigator.platform.indexOf('Win') > -1) {
      ps = new PerfectScrollbar(this.sidebar.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      })
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.customerdata.features !== prevProps.customerdata.features) {
      this.setState({ featureOptions: this.props.customerdata.features })
    }
    // the saved list is back from the prefs listener: show it instead of the pending copy
    const pending = this.state.pendingFavorites
    if (pending && JSON.stringify(this.userFavorites()) === JSON.stringify(pending)) {
      this.setState({ pendingFavorites: null })
    }
  }

  componentWillUnmount() {
    // we need to destroy the false scrollbar when we navigate
    // to a page that doesn't have this component rendered
    if (navigator.platform.indexOf('Win') > -1) {
      ps.destroy()
    }
  }
  render() {
    const routes = this.props.routes || []
    const mainRoutes = routes.filter((r) => r.path !== SETTINGS_PATH)
    const settingsRoutes = routes.filter((r) => r.path === SETTINGS_PATH)
    // Logout lives under the same layout as the rest of this role's routes.
    const layoutRoute = routes.find((r) => r.layout)
    const logoutPath = (layoutRoute ? layoutRoute.layout : '/admin') + '/logout'
    const email = this.props.user ? this.props.user.email : ''

    return (
      <div className='sidebar' data-color={this.props.bgColor} data-active-color={this.props.activeColor}>
        <div className='logo sidebar-header'>
          <img src={logo} alt='cintoia-logo' className='sidebar-header-logo' />
          <div className='sidebar-header-text'>
            <a href='https://www.cintoia.com' className='sidebar-header-brand'>
              CINTOIA
            </a>
            <span className='sidebar-header-venue' title={this.props.displayName}>
              {this.props.displayName}
            </span>
          </div>
        </div>

        <div className='sidebar-wrapper' ref={this.sidebar}>
          {this.renderFavorites()}
          <Nav>{this.createLinks(mainRoutes)}</Nav>
        </div>

        <div className='sidebar-footer'>
          {settingsRoutes.length > 0 ? (
            <div className='sidebar-footer-settings'>
              <Nav>{this.createLinks(settingsRoutes, false, false)}</Nav>
            </div>
          ) : null}
          <SidebarAccount email={email} displayName={this.props.displayName} logoutPath={logoutPath} />
        </div>
        {/* Keeps the footer clear of the mobile browser's bottom bar. */}
        {isMobile ? (<div className='sidebar-mobile-spacer' style={{ height: 60, flex: 'none' }}></div>) : null}
      </div>
    )
  }
}

// Wrapper to provide location prop (replaces withRouter from v5)
function SidebarWithRouter(props) {
  const location = useLocation()
  return <Sidebar {...props} location={location} />
}

// withAppdata brings appdata.userpreferences (the favourites) and the firebase prop
export default withAuthentication(withAppdata(SidebarWithRouter))
